import { FIELD_NAMES } from '@/pages/inventory/inventoryStatus/constants'

/** ST02 필터 폼 값. 다건 입력(브랜드명·바코드명·SKU_ID·스타일번호)만 배열이고 나머지는 단건이다. */
export interface InventoryStatusFormValues {
  [FIELD_NAMES.logisticsServiceTypes]?: string
  [FIELD_NAMES.shipperNames]?: string
  [FIELD_NAMES.whNm]?: string
  [FIELD_NAMES.brandNames]?: string[]
  [FIELD_NAMES.gdsCd]?: string
  [FIELD_NAMES.barcodes]?: string[]
  [FIELD_NAMES.skuIds]?: string[]
  [FIELD_NAMES.styleNumbers]?: string[]
}

/**
 * 메뉴 세션·URL 쿼리에 싣는 검색 조건 모양. (MOMS 공통 UX — 다른 메뉴를 다녀와도 마지막 조회 조건이 남는다)
 *
 * 필드 이름은 폼 값 키를 그대로 쓴다 — URL 만 봐도 뜻이 통한다.
 * 다건 입력은 `Utils.qsStringify` 가 `arrayFormat: 'comma'` 로 직렬화하고 `useParsedQuery` 가
 * `comma: true` 로 되돌린다. 각 값에 콤마가 들어가면 깨지지만, 다건 입력 위젯 자체가 콤마를 구분자로
 * 쓰기 때문에(MultiValueInput) 콤마를 포함한 값은 애초에 만들어지지 않는다.
 * 다만 항목이 하나면 파서가 배열이 아닌 문자열을 주므로 복원할 때 양쪽을 모두 받는다.
 */
export interface InventoryStatusSearchQuery {
  logisticsServiceTypes?: string
  shipperNames?: string
  whNm?: string
  /** IVL 모드에서만 복원한다. 기존 센터명과 섞지 않는다. */
  warehouseCode?: string
  brandNames?: string[]
  gdsCd?: string
  barcodes?: string[]
  skuIds?: string[]
  styleNumbers?: string[]
}

/** 값이 있을 때만 키를 싣는다 — 빈 값을 넣으면 쿼리스트링만 길어진다. */
const put = <T>(key: string, value: T | undefined) => (value === undefined ? {} : { [key]: value })

const trimmedText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const text = value.trim()
  return text || undefined
}

/** 문자열 배열만 남긴다. 파서가 단건을 문자열로 돌려주므로 그것도 배열로 받아 준다. */
const trimmedList = (value: unknown): string[] | undefined => {
  const raw = Array.isArray(value) ? value : [value]
  const list = raw.map(trimmedText).filter((v): v is string => v !== undefined)
  return list.length > 0 ? list : undefined
}

/**
 * 세션·URL 에 저장할 형태로 만든다.
 *
 * 화주명·센터명의 '전체'(빈 문자열)는 값이 없는 것과 같아 싣지 않는다 — 복원할 때 기본값이 '전체'라 결과가 같다.
 */
export const toSearchQuery = (values: InventoryStatusFormValues): InventoryStatusSearchQuery => ({
  ...put('logisticsServiceTypes', trimmedText(values[FIELD_NAMES.logisticsServiceTypes])),
  ...put('shipperNames', trimmedText(values[FIELD_NAMES.shipperNames])),
  ...put('whNm', trimmedText(values[FIELD_NAMES.whNm])),
  ...put('brandNames', trimmedList(values[FIELD_NAMES.brandNames])),
  ...put('gdsCd', trimmedText(values[FIELD_NAMES.gdsCd])),
  ...put('barcodes', trimmedList(values[FIELD_NAMES.barcodes])),
  ...put('skuIds', trimmedList(values[FIELD_NAMES.skuIds])),
  ...put('styleNumbers', trimmedList(values[FIELD_NAMES.styleNumbers])),
})

/**
 * 저장된 쿼리를 폼 값으로 되돌린다. 쿼리에 없는 항목은 화면 기본값을 쓴다.
 *
 * 조작된 URL(배열이 와야 할 자리에 객체 등)이 폼을 깨뜨리지 않도록 타입이 맞는 값만 받는다.
 */
export const fromSearchQuery = (query: Partial<InventoryStatusSearchQuery>, defaults: InventoryStatusFormValues): InventoryStatusFormValues => ({
  [FIELD_NAMES.logisticsServiceTypes]: trimmedText(query.logisticsServiceTypes) ?? defaults[FIELD_NAMES.logisticsServiceTypes],
  [FIELD_NAMES.shipperNames]: trimmedText(query.shipperNames) ?? defaults[FIELD_NAMES.shipperNames],
  [FIELD_NAMES.whNm]: trimmedText(query.whNm) ?? defaults[FIELD_NAMES.whNm],
  [FIELD_NAMES.brandNames]: trimmedList(query.brandNames),
  [FIELD_NAMES.gdsCd]: trimmedText(query.gdsCd),
  [FIELD_NAMES.barcodes]: trimmedList(query.barcodes),
  [FIELD_NAMES.skuIds]: trimmedList(query.skuIds),
  [FIELD_NAMES.styleNumbers]: trimmedList(query.styleNumbers),
})
